import React, { useEffect, useState } from 'react';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import OrderDetailsModal from '../../components/OrderDetailsModal';
import { useToast } from '../../context/ToastContext';
import { fetchOrders, archiveCompletedOrders } from '../../lib/adminApi';
import { exportToCsv, downloadFilename } from '../../lib/csvExport';

const orderStatusFilters = [
  { value: 'all', label: 'All orders' },
  { value: 'pending', label: 'Pending' },
  { value: 'confirmed', label: 'Confirmed' },
  { value: 'in_progress', label: 'In progress' },
  { value: 'ready_for_delivery', label: 'Ready for delivery' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
];

const statusStyles = {
  pending: 'bg-amber-100 text-amber-700',
  confirmed: 'bg-blue-100 text-blue-700',
  in_progress: 'bg-sky-100 text-sky-700',
  ready_for_delivery: 'bg-indigo-100 text-indigo-700',
  completed: 'bg-emerald-100 text-emerald-700',
  cancelled: 'bg-rose-100 text-rose-700',
};

const orderExportColumns = [
  { key: 'id', label: 'Order ID' },
  { key: 'status', label: 'Status' },
  { key: 'total_amount', label: 'Total' },
  { key: 'event_date', label: 'Event Date' },
  { key: 'guests', label: 'Guests' },
  { key: 'customer_email', label: 'Customer' },
  { key: 'caterer_name', label: 'Caterer' },
];

export default function OrderManagementPage() {
  const toast = useToast();
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState('all');
  const [includeArchived, setIncludeArchived] = useState(false);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [detailsOrderId, setDetailsOrderId] = useState(null);
  const [archiving, setArchiving] = useState(false);

  useEffect(() => {
    let isMounted = true;

    async function loadOrders() {
      setLoading(true);
      setError(null);

      try {
        const data = await fetchOrders({
          status: statusFilter,
          includeArchived,
        });
        if (isMounted) {
          setOrders(data);
        }
      } catch (err) {
        console.error('Failed to load orders', err);
        if (isMounted) {
          setError(err instanceof Error ? err.message : 'Failed to load orders');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    }

    loadOrders();

    return () => {
      isMounted = false;
    };
  }, [statusFilter, includeArchived]);

  const handleArchiveCompleted = async () => {
    setArchiving(true);
    try {
      const { archived } = await archiveCompletedOrders();
      toast.success(archived > 0 ? `Archived ${archived} completed orders` : 'No completed orders to archive');
      if (archived > 0) {
        const data = await fetchOrders({ status: statusFilter, includeArchived });
        setOrders(data);
      }
    } catch (err) {
      toast.error(err?.message ?? 'Failed to archive orders');
    } finally {
      setArchiving(false);
    }
  };

  return (
    <div className="space-y-6">
      <header className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
        <div>
            <h2 className="text-2xl font-semibold text-foreground">Order Operations</h2>
          <p className="text-sm text-muted-foreground">
            Investigate fulfillment risk, escrow balances, and lifecycle speed for every order.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => {
              const rows = orders.map((o) => ({
                id: o.id,
                status: o.status,
                total_amount: o.total_amount,
                event_date: o.event_date ? format(new Date(o.event_date), 'yyyy-MM-dd') : '',
                guests: o.guests ?? '',
                customer_email: o.customer?.email ?? '',
                caterer_name: o.caterer?.business_name ?? '',
              }));
              if (!rows.length) {
                toast.info('No orders to export');
                return;
              }
              exportToCsv(rows, orderExportColumns, downloadFilename('orders'));
              toast.success(`Exported ${rows.length} orders`);
            }}
            className="inline-flex items-center gap-2 rounded-md border border-border bg-card px-3 py-2 text-sm font-medium text-foreground shadow-sm hover:bg-muted"
          >
            <Icon name="Download" size={16} /> Export
          </button>
          <button
            onClick={handleArchiveCompleted}
            disabled={archiving}
            className="inline-flex items-center gap-2 rounded-md border border-border bg-card px-3 py-2 text-sm font-medium text-foreground shadow-sm hover:bg-muted disabled:opacity-60"
          >
            <Icon name="Archive" size={16} /> {archiving ? 'Archiving…' : 'Archive completed'}
          </button>
          <button
            onClick={() => navigate('/admin/escalations')}
            className="inline-flex items-center gap-2 rounded-md bg-primary px-3 py-2 text-sm font-semibold text-primary-foreground shadow-sm hover:bg-primary/90"
          >
            <Icon name="BellRing" size={16} /> Escalation centre
          </button>
        </div>
      </header>

      {error && (
        <div className="rounded-lg border border-destructive/40 bg-destructive/10 p-4 text-sm text-destructive">
          {error}
        </div>
      )}

      <div className="rounded-xl border border-border bg-card shadow-sm">
        <div className="flex flex-wrap items-center gap-2 border-b border-border/80 px-4 py-4">
          <label className="flex items-center gap-2 text-sm text-muted-foreground">
            <input
              type="checkbox"
              checked={includeArchived}
              onChange={(e) => setIncludeArchived(e.target.checked)}
              className="rounded border-border"
            />
            Show archived
          </label>
          {orderStatusFilters.map((filter) => (
            <button
              key={filter.value}
              onClick={() => setStatusFilter(filter.value)}
              className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
                statusFilter === filter.value ? 'bg-primary text-primary-foreground shadow' : 'bg-muted text-muted-foreground hover:text-foreground'
              }`}
            >
              {filter.label}
            </button>
          ))}
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-border text-sm">
            <thead className="bg-muted/30 text-muted-foreground">
              <tr>
                <th className="px-4 py-3 text-left font-semibold">Order</th>
                <th className="px-4 py-3 text-left font-semibold">Customer</th>
                <th className="px-4 py-3 text-left font-semibold">Caterer</th>
                <th className="px-4 py-3 text-left font-semibold">Event Date</th>
                <th className="px-4 py-3 text-left font-semibold">Guests</th>
                <th className="px-4 py-3 text-left font-semibold">Amount</th>
                <th className="px-4 py-3 text-left font-semibold">Escrow</th>
                <th className="px-4 py-3 text-left font-semibold">Status</th>
                <th className="px-4 py-3 text-right font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border/80">
              {loading && (
                <tr>
                  <td colSpan={9} className="px-6 py-6 text-center text-muted-foreground">
                    Loading orders…
                  </td>
                </tr>
              )}

              {!loading && orders.length === 0 && (
                <tr>
                  <td colSpan={9} className="px-6 py-6 text-center text-muted-foreground">
                    No orders match the selected filter.
                  </td>
                </tr>
              )}

              {orders.map((order) => (
                <tr key={order.id} className="hover:bg-muted/30">
                  <td className="px-4 py-3">
                    <div className="flex flex-col gap-0.5">
                      <span className="font-semibold text-foreground">{String(order.id).slice(0, 8)}…</span>
                      {order.archived_at && (
                        <span className="inline-flex w-fit rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">Archived</span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">{order.customer?.email ?? '—'}</td>
                  <td className="px-4 py-3 text-muted-foreground">{order.caterer?.business_name ?? '—'}</td>
                  <td className="px-4 py-3 text-muted-foreground">
                    {order.event_date ? format(new Date(order.event_date), 'dd MMM yyyy') : '—'}
                  </td>
                  <td className="px-4 py-3 text-foreground">{order.guests ?? '—'}</td>
                  <td className="px-4 py-3 font-semibold text-foreground">
                    {order.total_amount ? `R${Number(order.total_amount).toLocaleString()}` : '—'}
                  </td>
                  <td className="px-4 py-3">
                    {order.escrow ? (
                      <div className="flex flex-col gap-1">
                        <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ${
                          order.escrow.escrow_status === 'released' ? 'bg-emerald-100 text-emerald-700' :
                          order.escrow.escrow_status === 'escrowed' ? 'bg-blue-100 text-blue-700' :
                          'bg-amber-100 text-amber-700'
                        }`}>
                          {order.escrow.escrow_status || 'pending'}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          R {Number(order.escrow.caterer_net_amount || 0).toLocaleString()}
                        </span>
                      </div>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${
                      statusStyles[order.status] ?? 'bg-muted text-foreground'
                    }`}>
                      {order.status.replace(/_/g, ' ')}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => setDetailsOrderId(order.id)}
                        className="inline-flex items-center gap-1 rounded-md border border-border px-3 py-1 text-xs font-semibold text-muted-foreground hover:bg-muted"
                      >
                        <Icon name="Eye" size={14} /> Details
                      </button>
                      <button
                        onClick={() => navigate(`/admin/escalations?orderId=${order.id}`)}
                        className="inline-flex items-center gap-1 rounded-md bg-primary px-3 py-1 text-xs font-semibold text-primary-foreground hover:bg-primary/90"
                      >
                        <Icon name="AlertTriangle" size={14} /> Escalate
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {detailsOrderId && (
        <OrderDetailsModal orderId={detailsOrderId} onClose={() => setDetailsOrderId(null)} />
      )}
    </div>
  );
}
